import { useRef, useEffect, useState, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import { ArrowRight, Home as HomeIcon, Building2, Palette, Hammer, Key, FileSpreadsheet, ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Import the new Hero Carousel
import HeroCarousel from '../components/HeroCarousel';

const WHATSAPP_NUMBER = '918880331224';

// Services data
const services = [
  { icon: HomeIcon, title: 'Residential Construction', description: 'Custom homes built with precision and quality materials for your family.' },
  { icon: Building2, title: 'Commercial Construction', description: 'Modern commercial spaces designed for productivity and growth.' },
  { icon: Palette, title: 'Interior Design', description: 'Transform your space with elegant and functional interior designs.' },
  { icon: Hammer, title: 'Renovation', description: 'Breathe new life into your existing property with expert renovation.' },
  { icon: Key, title: 'Turnkey Projects', description: 'Complete solutions from foundation to finishing, hassle-free.' },
  { icon: FileSpreadsheet, title: 'Architectural Planning', description: 'Innovative architectural designs that blend form with function.' },
];

// Projects data
const projects = [
  { 
    id: 1, 
    title: 'Modern Villa Residence', 
    location: 'Kalaburagi', 
    type: 'Residential',
    image: 'https://images.pexels.com/photos/17174767/pexels-photo-17174767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  { 
    id: 2, 
    title: 'Commercial Complex', 
    location: 'Gulbarga', 
    type: 'Commercial',
    image: 'https://images.unsplash.com/photo-1571440727633-ca6b05da2008?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwZHVzayUyMGFyY2hpdGVjdHVyZSUyMGdvbGQlMjBsaWdodGluZ3xlbnwwfHx8fDE3NzIwMTAxNjV8MA&ixlib=rb-4.1.0&q=85'
  },
  { 
    id: 3, 
    title: 'Luxury Interior', 
    location: 'Aland', 
    type: 'Interior',
    image: 'https://images.unsplash.com/photo-1762544968153-b9b47435fefd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBkYXJrJTIwZ29sZCUyMGFjY2VudHN8ZW58MHx8fHwxNzcyMDEwMTY3fDA&ixlib=rb-4.1.0&q=85'
  },
];

// Stats data
const stats = [
  { value: 150, suffix: '+', label: 'Projects Completed' },
  { value: 200, suffix: '+', label: 'Happy Clients' },
  { value: 12, suffix: '+', label: 'Years Experience' },
  { value: 25, suffix: '+', label: 'Ongoing Projects' },
];

// Testimonials data
const testimonials = [
  {
    id: 1,
    name: 'Rajesh Kumar',
    location: 'Kalaburagi',
    review: 'Akruti Engineers built our dream home exactly as we envisioned. Their attention to detail and quality of work is exceptional.',
    rating: 5,
  },
  {
    id: 2,
    name: 'Priya Sharma',
    location: 'Gulbarga',
    review: 'The interior design team transformed our office space completely. Professional service and timely delivery.',
    rating: 5,
  },
  {
    id: 3,
    name: 'Mohammed Iqbal',
    location: 'Aland',
    review: 'Best construction company in the region. Affordable prices without compromising on quality.',
    rating: 5,
  },
];

// Counter Component
function Counter({ value, suffix, label }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <div ref={ref} className="text-center">
      <div className="font-heading text-5xl md:text-6xl text-[#1a1a1a]">
        {count}{suffix}
      </div>
      <p className="text-gray-600 text-sm mt-2 uppercase tracking-wider">{label}</p>
    </div>
  );
}

// Service Card Component
function ServiceCard({ service, index }) {
  const Icon = service.icon;
  
  return (
    <div
      className="relative bg-[#1a1a1a] p-8 rounded-2xl shadow-lg group cursor-pointer hover:shadow-xl hover:-translate-y-2 transition-all duration-300 overflow-hidden"
      data-testid={`service-card-${index}`}
    >
      {/* Golden stripe on left */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-[#C9A646] to-[#E6C36A]" />
      
      {/* 3D shadow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl" />
      
      <div className="relative z-10">
        <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#C9A646] to-[#E6C36A] mb-6 group-hover:scale-110 transition-transform duration-300 rounded-xl shadow-lg shadow-[#C9A646]/30">
          <Icon size={28} className="text-[#1a1a1a]" />
        </div>
        <h3 className="font-heading text-2xl text-white font-bold mb-3">{service.title}</h3>
        <p className="text-white/70 text-sm leading-relaxed">{service.description}</p>
      </div>
    </div>
  );
}

// Project Card Component
function ProjectCard({ project, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      viewport={{ once: true }}
      className="project-card group"
      data-testid={`project-card-${index}`}
    >
      <img 
        src={project.image} 
        alt={project.title}
        className="w-full h-80 object-cover"
        loading="lazy"
      />
      <div className="project-card-content">
        <span className="text-[#C9A646] text-xs uppercase tracking-wider">{project.type}</span>
        <h3 className="font-heading text-2xl text-gray-900 mt-1">{project.title}</h3>
        <p className="text-gray-600 text-sm mt-1">{project.location}</p>
        <Link 
          to="/projects"
          className="inline-flex items-center gap-2 text-[#C9A646] text-sm mt-4 group-hover:gap-3 transition-all"
        >
          View Details <ArrowRight size={16} />
        </Link>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const handleConsultation = () => {
    const message = encodeURIComponent('Hello! I would like to book a free consultation for my construction/interior project.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="noise-overlay" data-testid="home-page">
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-[#ececec]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              What We Offer
            </span>
            <h2 className="font-heading text-4xl md:text-6xl text-gray-900 mt-4">
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.title} service={service} index={index} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-[#C9A646] hover:gap-3 transition-all"
              data-testid="view-all-services-link"
            >
              View All Services <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-24 md:py-32 bg-[#f5f5f5]" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-16"
          >
            <div>
              <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
                Portfolio
              </span>
              <h2 className="font-heading text-4xl md:text-6xl text-gray-900 mt-4">
                Featured Projects
              </h2>
            </div>
            <Link 
              to="/projects"
              className="inline-flex items-center gap-2 text-[#C9A646] mt-6 md:mt-0 hover:gap-3 transition-all"
              data-testid="view-all-projects-link"
            >
              View All Projects <ArrowRight size={18} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 md:py-32 bg-[#ececec] border-y border-gray-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat) => (
              <Counter key={stat.label} {...stat} />
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-[#f5f5f5]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              How We Work
            </span>
            <h2 className="font-heading text-4xl md:text-6xl text-gray-900 mt-4">
              Our Process
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We understand your vision and requirements' },
              { step: '02', title: 'Planning & Design', desc: 'Expert architects create detailed plans' },
              { step: '03', title: 'Execution', desc: 'Skilled craftsmen bring designs to life' },
              { step: '04', title: 'Delivery', desc: 'Quality-checked handover of your space' },
            ].map((item, index) => (
              <div
                key={item.step}
                className="relative"
              >
                <div className="text-center">
                  <span className="font-heading text-6xl font-bold text-[#1a1a1a]">{item.step}</span>
                  <h3 className="font-heading text-2xl font-bold text-gray-900 mt-4">{item.title}</h3>
                  <p className="text-gray-600 text-sm mt-2">{item.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-[#C9A646] to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 bg-[#ececec]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              Testimonials
            </span>
            <h2 className="font-heading text-4xl md:text-6xl text-gray-900 mt-4">
              What Clients Say
            </h2>
          </motion.div>

          <div className="relative max-w-3xl mx-auto">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-gradient-to-br from-[#C9A646] to-[#E6C36A] p-10 rounded-2xl shadow-xl text-center"
            >
              <Quote size={48} className="text-[#1a1a1a]/20 mx-auto mb-6" />
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} size={18} fill="#1a1a1a" className="text-[#1a1a1a]" />
                ))}
              </div>
              <p className="text-[#1a1a1a] text-lg md:text-xl leading-relaxed italic">
                "{testimonials[currentTestimonial].review}"
              </p>
              <div className="mt-8">
                <h4 className="font-heading text-xl text-[#1a1a1a]">
                  {testimonials[currentTestimonial].name}
                </h4>
                <p className="text-[#1a1a1a]/70 text-sm mt-1">
                  {testimonials[currentTestimonial].location}
                </p>
              </div>
            </motion.div>

            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 hover:border-[#C9A646] hover:text-[#C9A646] transition-colors"
                data-testid="prev-testimonial-btn"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 flex items-center justify-center border border-gray-200 hover:border-[#C9A646] hover:text-[#C9A646] transition-colors"
                data-testid="next-testimonial-btn"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#C9A646] to-[#E6C36A]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div>
            <h2 className="font-heading text-4xl md:text-6xl text-[#1a1a1a]">
              Start Your Dream Project Today
            </h2>
            <p className="text-[#1a1a1a]/80 text-lg mt-6 max-w-xl mx-auto">
              Let's build something <span className="font-bold">extraordinary together</span>. Get a free consultation with our experts.
            </p>
            <button
              onClick={handleConsultation}
              className="mt-10 bg-[#1a1a1a] text-white font-semibold px-10 py-5 rounded-xl hover:bg-[#333] transition-colors inline-flex items-center gap-2"
              data-testid="cta-consultation-btn"
            >
              Get Free Consultation
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
